import apiClient from '../../../services/api/client';
import type {
  SalesLead,
  SalesLeadListResponse,
  SalesComment,
  SalesLeadExample,
  SalesCompanyProfile,
  SalesStatusHistoryEntry,
  LeadGenerateParams,
  GeneratedLead,
} from '../types/sales';

const BASE = '/api/sales';

export const salesApi = {
  // ── Leads ────────────────────────────────────────────
  listLeads: (params?: {
    status?: string;
    archived?: boolean;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    per_page?: number;
  }) =>
    apiClient
      .get<SalesLeadListResponse>(`${BASE}/leads`, { params })
      .then((r) => r.data),

  listArchivedLeads: (page = 1, per_page = 50) =>
    apiClient
      .get<SalesLeadListResponse>(`${BASE}/leads/archived`, { params: { page, per_page } })
      .then((r) => r.data),

  getLead: (id: string) =>
    apiClient.get<SalesLead>(`${BASE}/leads/${id}`).then((r) => r.data),

  createLead: (data: {
    company_name: string;
    website_url?: string;
    business_info?: string;
    inquiry?: string;
    contact_email?: string;
    contact_name?: string;
    contact_phone?: string;
  }) =>
    apiClient.post<SalesLead>(`${BASE}/leads`, data).then((r) => r.data),

  updateLead: (
    id: string,
    data: {
      status?: string;
      sales_rating?: number;
      archived?: boolean;
      pipeline_order?: number;
      contact_phone?: string | null;
    },
  ) =>
    apiClient.patch<SalesLead>(`${BASE}/leads/${id}`, data).then((r) => r.data),

  toggleArchive: (id: string) =>
    apiClient.post<SalesLead>(`${BASE}/leads/${id}/archive`).then((r) => r.data),

  refreshAiRating: (id: string) =>
    apiClient.post<SalesLead>(`${BASE}/leads/${id}/refresh-ai-rating`).then((r) => r.data),

  // ── Comments ─────────────────────────────────────────
  listComments: (leadId: string) =>
    apiClient.get<SalesComment[]>(`${BASE}/leads/${leadId}/comments`).then((r) => r.data),

  createComment: (leadId: string, content: string, replyToId?: string) =>
    apiClient
      .post<SalesComment>(`${BASE}/leads/${leadId}/comments`, {
        content,
        reply_to_id: replyToId || null,
      })
      .then((r) => r.data),

  askAgent: async (
    leadId: string,
    question: string,
    onChunk: (text: string) => void,
  ): Promise<string> => {
    const token = localStorage.getItem('quazi_access_token');
    const baseURL = apiClient.defaults.baseURL || '';
    const response = await fetch(`${baseURL}${BASE}/leads/${leadId}/comments/agent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ question }),
    });

    if (!response.ok) throw new Error(`Agent request failed: ${response.status}`);
    if (!response.body) throw new Error('No response body');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      fullResponse += chunk;
      onChunk(fullResponse);
    }

    return fullResponse;
  },

  // ── History ──────────────────────────────────────────
  getHistory: (leadId: string) =>
    apiClient
      .get<SalesStatusHistoryEntry[]>(`${BASE}/leads/${leadId}/history`)
      .then((r) => r.data),

  // ── Examples ─────────────────────────────────────────
  listExamples: () =>
    apiClient.get<SalesLeadExample[]>(`${BASE}/examples`).then((r) => r.data),

  createExample: (data: { type: 'good' | 'bad'; company_url: string; explanation: string }) =>
    apiClient.post<SalesLeadExample>(`${BASE}/examples`, data).then((r) => r.data),

  deleteExample: (id: string) => apiClient.delete(`${BASE}/examples/${id}`),

  // ── Profile ──────────────────────────────────────────
  getProfile: () =>
    apiClient.get<SalesCompanyProfile | null>(`${BASE}/profile`).then((r) => r.data),

  updateProfile: (description: string) =>
    apiClient
      .put<SalesCompanyProfile>(`${BASE}/profile`, { description })
      .then((r) => r.data),

  // ── Lead Generator ───────────────────────────────────
  generate: (params: LeadGenerateParams): Promise<Response> => {
    const token = localStorage.getItem('quazi_access_token');
    const baseURL = apiClient.defaults.baseURL || '';
    return fetch(`${baseURL}${BASE}/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(params),
    });
  },

  acceptLeads: (leads: Omit<GeneratedLead, 'quality'>[]) =>
    apiClient
      .post<SalesLead[]>(`${BASE}/accept`, { leads })
      .then((r) => r.data),
};
